import { Mapper } from '../../../../../core/base';
import {
  ClosedDayWebEntity,
  DynamicAddressFieldWebEntity,
  KeyValueDataWebEntity,
  LocationTagWebEntity,
  LocationWebEntity,
  OpeningHourWebEntity,
  PrivateLocationWebEntity,
  ProductActivityHourWebEntity,
  ProductEntity,
  SelectedProductActivityWebEntity,
  SelectedProductsWebEntity,
  ServiceHourWebEntity,
  SubLocationWebEntity,
  TagEntity,
} from './location-web-entity';
import {
  ClosedDay,
  DynamicAddressField,
  KeyValueData,
  Location,
  LocationTag,
  OpeningHour,
  PrivateLocation,
  Product,
  ProductActivityHour,
  SelectedProductActivity,
  SelectedProducts,
  ServiceHour,
  SubLocation,
  Tag,
} from '../../../../../core/domain/models';
import { DateTime, Duration } from 'luxon';
import { DaysOfAWeek } from '../../../../../core/domain/enums';

export class LocationWebRepositoryMapper extends Mapper<
  LocationWebEntity,
  Location
> {
  mapFrom(param: LocationWebEntity): Location {
    return {
      id: param.id,
      publicLocationId: param.publicLocationId,
      name: param.name,
      longitude: param.longitude,
      latitude: param.latitude,
      address: param.address,
      partialAddress: param.partialAddress,
      postalCode: param.postalCode,
      city: param.city,
      country: param.country,
      isEnable: param.isEnable,
      title: param.title,
      isInfrastructureLocation: param.isInfrastructureLocation,
      products: param.products?.map(
        (product: ProductEntity): Product => ({
          id: product.productID,
          name: product.productName,
          daoLocationKey: product.daoLocationKey,
          productKeyMapping: product.productKeyMapping,
          segment : product.segment,
          shouldVisitIfNoService: product.shouldVisitIfNoService,
          keyValueData: product.keyValueData?.map((keyValueData) => ({
            key: keyValueData.key,
            name: keyValueData.name,
          })),
        })
      ),
      tags: param.tags?.map(
        (tag: TagEntity): Tag => ({
          id: tag.tagID,
          name: tag.tagName,
        })
      ),
      privateLocation: param.privateLocation?.map(
        (privateLocation: PrivateLocationWebEntity): PrivateLocation => ({
          id: privateLocation.id,
          locationId: privateLocation.locationId,
          title: privateLocation.title,
          tenantLocationId: privateLocation.tenantLocationId,
          buildingBlockId: privateLocation.buildingBlockId,
          products: privateLocation.products.map(
            (product: ProductEntity): Product => ({
              id: product.productID,
              name: product.productName,
              daoLocationKey: product.daoLocationKey,
              productKeyMapping: product.productKeyMapping,
              segment : product.segment,
              shouldVisitIfNoService: product.shouldVisitIfNoService,
              keyValueData: product.keyValueData?.map((keyValueData) => ({
                key: keyValueData.key,
                name: keyValueData.name,
              })),
            })
          ),
          locationTypeId: privateLocation.locationTypeId,
          stopTime: Duration.fromISOTime(privateLocation.stopTime),
          publicLocationId: privateLocation.publicLocationId,
          tags: privateLocation.tags?.map(
            (locationTag: LocationTagWebEntity): LocationTag => ({
              tagID: locationTag.tagID,
              tagName: locationTag.tagName,
            })
          ),
          additionalInfo: {
            openingHours: privateLocation.additionalInfo.openingHours?.map(
              (openingHour: OpeningHourWebEntity): OpeningHour => ({
                day: openingHour.day,
                productActivityHoursID:openingHour.productActivityHoursID,
                fromTime: Duration.fromISOTime(openingHour.fromTime),
                toTime: Duration.fromISOTime(openingHour.toTime),
                isEnabled: openingHour.isEnabled,
              })
            ),
            selectedProducts:
              privateLocation.additionalInfo.selectedProducts?.map(
                (item: SelectedProductsWebEntity): SelectedProducts => ({
                  productId: item.productId,
                  serviceHours: item.serviceHours?.map(
                    (serviceHour: ServiceHourWebEntity): ServiceHour => ({
                      forProductActivity: serviceHour.forProductActivity,
                      day: Object.values(DaysOfAWeek)[
                        +serviceHour.day
                      ].toString(),
                      isEnabled: serviceHour.isEnabled,
                      productActivityHours:
                        serviceHour.productActivityHours?.map(
                          (
                            productActivityHour: ProductActivityHourWebEntity
                          ): ProductActivityHour => ({
                            productActivityHoursID:productActivityHour.productActivityHoursID,
                            productActivityId:
                              productActivityHour.productActivityId,
                            fromTime: Duration.fromISOTime(
                              productActivityHour.fromTime
                            ),
                            toTime: Duration.fromISOTime(
                              productActivityHour.toTime
                            ),
                            stopTime: Duration.fromISOTime(
                              productActivityHour.stopTime
                            ),
                            isForAllProducts:
                              productActivityHour.isForAllProducts,
                          })
                        ),
                    })
                  ),
                  selectedProductActivities:
                    item.selectedProductActivities?.map(
                      (
                        selectedProductActivity: SelectedProductActivityWebEntity
                      ): SelectedProductActivity => ({
                        activityName: selectedProductActivity.activityName,
                        activityId: selectedProductActivity.activityId,
                      })
                    ),
                })
              ),
            dynamicAddressFields:
              privateLocation.additionalInfo.dynamicAddressFields?.map(
                (
                  dynamicAddressField: DynamicAddressFieldWebEntity
                ): DynamicAddressField => ({
                  key: dynamicAddressField.key,
                  name: dynamicAddressField.name,
                })
              ),
            keyValueData: privateLocation.additionalInfo.keyValueData?.map(
              (keyValueData: KeyValueDataWebEntity): KeyValueData => ({
                key: keyValueData.key,
                name: keyValueData.name,
              })
            ),
            subLocations: privateLocation.additionalInfo.subLocations?.map(
              (subLocation: SubLocationWebEntity): SubLocation => ({
                name: subLocation.name,
                description: subLocation.description,
                geoLocation: {
                  latitude: subLocation.geoLocation.latitude,
                  longitude: subLocation.geoLocation.longitude,
                },
                isEnabled: subLocation.isEnabled,
                isAlternateLocation: subLocation.isAlternateLocation,
              })
            ),
            closedDays: privateLocation.additionalInfo.closedDays?.map(
              (closedDay: ClosedDayWebEntity): ClosedDay => ({
                title: closedDay.title,
                fromDate: DateTime.fromISO(closedDay.fromDate),
                toDate: DateTime.fromISO(closedDay.toDate),
              })
            ),
          },
        })
      ),
    };
  }

  mapTo(param: Location): LocationWebEntity {
    return {
      id: param.id,
      publicLocationId: param.publicLocationId,
      name: param.name,
      longitude: param.longitude,
      latitude: param.latitude,
      address: param.address,
      partialAddress: param.partialAddress,
      postalCode: param.postalCode,
      city: param.city,
      country: param.country,
      isEnable: param.isEnable,
      title: param.title,
      privateLocation: param.privateLocation?.map(
        (privateLocation: PrivateLocation): PrivateLocationWebEntity => ({
          id: privateLocation.id,
          locationId: privateLocation.locationId,
          title: privateLocation.title,
          products: privateLocation.products.map(
            (product: Product): ProductEntity => ({
              productID: product.id,
              productName: product.name,
              daoLocationKey: product.daoLocationKey,
              productKeyMapping: product.productKeyMapping,
              segment : product.segment,
              shouldVisitIfNoService: product.shouldVisitIfNoService,
              keyValueData: product.keyValueData?.map((keyValueData) => ({
                key: keyValueData.key,
                name: keyValueData.name,
              })),
            })
          ),
          locationTypeId: privateLocation.locationTypeId,
          buildingBlockId: privateLocation.buildingBlockId,
          tenantLocationId: privateLocation.tenantLocationId,
          stopTime: privateLocation.stopTime.toISOTime() ?? '',
          publicLocationId: privateLocation.publicLocationId,
          tags: privateLocation.tags?.map(
            (locationTag: LocationTagWebEntity): LocationTag => ({
              tagID: locationTag.tagID,
              tagName: locationTag.tagName,
            })
          ),
          additionalInfo: {
            openingHours: privateLocation.additionalInfo.openingHours.map(
              (openingHour: OpeningHour): OpeningHourWebEntity => ({
                productActivityHoursID:openingHour.productActivityHoursID,
                day: openingHour.day,
                fromTime: openingHour.fromTime.toISOTime() ?? '',
                toTime: openingHour.toTime.toISOTime() ?? '',
                isEnabled: openingHour.isEnabled,
              })
            ),
            selectedProducts:
              privateLocation.additionalInfo.selectedProducts?.map(
                (item: SelectedProducts): SelectedProductsWebEntity => ({
                  productId: item.productId,
                  serviceHours: item.serviceHours?.map(
                    (serviceHour: ServiceHour): ServiceHourWebEntity => ({
                      forProductActivity: serviceHour.forProductActivity,
                      day: +Object.values(DaysOfAWeek)[
                        Object.values(DaysOfAWeek).indexOf(serviceHour.day)
                      ],
                      isEnabled: serviceHour.isEnabled,
                      productActivityHours:
                        serviceHour.productActivityHours?.map(
                          (
                            productActivityHour: ProductActivityHour
                          ): ProductActivityHourWebEntity => ({
                            productActivityHoursID:productActivityHour.productActivityHoursID,
                            productActivityId:
                              productActivityHour.productActivityId,
                            fromTime:
                              productActivityHour.fromTime.toISOTime() ?? '',
                            toTime:
                              productActivityHour.toTime.toISOTime() ?? '',
                            stopTime:
                              productActivityHour.stopTime.toISOTime() ?? '',
                            isForAllProducts:
                              productActivityHour.isForAllProducts,
                          })
                        ),
                    })
                  ),
                  selectedProductActivities:
                    item.selectedProductActivities?.map(
                      (
                        selectedProductActivity: SelectedProductActivity
                      ): SelectedProductActivityWebEntity => ({
                        activityName: selectedProductActivity.activityName,
                        activityId: selectedProductActivity.activityId,
                      })
                    ),
                })
              ),
            dynamicAddressFields:
              privateLocation.additionalInfo.dynamicAddressFields.map(
                (
                  dynamicAddressField: DynamicAddressField
                ): DynamicAddressFieldWebEntity => ({
                  key: dynamicAddressField.key,
                  name: dynamicAddressField.name,
                })
              ),
            keyValueData: privateLocation.additionalInfo.keyValueData.map(
              (keyValueData: KeyValueData): KeyValueDataWebEntity => ({
                key: keyValueData.key,
                name: keyValueData.name,
              })
            ),
            subLocations: privateLocation.additionalInfo.subLocations.map(
              (subLocation: SubLocation): SubLocationWebEntity => ({
                name: subLocation.name,
                description: subLocation.description,
                geoLocation: {
                  latitude: subLocation.geoLocation.latitude,
                  longitude: subLocation.geoLocation.longitude,
                },
                isEnabled: subLocation.isEnabled,
                isAlternateLocation: subLocation.isAlternateLocation,
              })
            ),
            closedDays: privateLocation.additionalInfo.closedDays.map(
              (closedDay: ClosedDay): ClosedDayWebEntity => ({
                title: closedDay.title,
                fromDate: closedDay.fromDate.toISODate() ?? '',
                toDate: closedDay.toDate.toISODate() ?? '',
              })
            ),
          },
        })
      ),
    };
  }
}
